import { useEffect, useState } from "react";
import { useField } from "formik";
import { Row, Col } from "react-bootstrap";
import SelectQuestTable from "./SelectQuestTable";
import QuestSelectorSkeleton from "./QuestSelectorSkeleton";
import { useGetQuestByCategory } from "../../../../hooks/teacher/quest/useGetQuestByCategory";

import "../classroom/SelectStudents.css";
import TableError from "../../common/TableError";

/**
 * @param {Object} param
 * @param {string} param.name - yup field name
 * @param {any[]} [param.initialSelectedQuests] - selected quests used in update flow
 */
const QuestSelector = ({ name }) => {
  const [field, meta, helpers] = useField(name);
  const {
    quests: groupedQuests,
    loading,
    error,
    getQuestByCategory,
  } = useGetQuestByCategory();

  const [displayedGroups, setDisplayedGroups] = useState({});

  // Initial Fetch
  useEffect(() => {
    getQuestByCategory();
  }, []);

  useEffect(() => {
    if (groupedQuests) {
      setDisplayedGroups(groupedQuests);
    }
  }, [groupedQuests]);

  // Toggle single quest
  const handleToggleQuest = (quest) => {
    const currentSelected = field.value || [];
    const isSelected = currentSelected.some((q) => q.id === quest.id);

    let newSelected;
    if (isSelected) {
      newSelected = currentSelected.filter((q) => q.id !== quest.id);
    } else {
      newSelected = [...currentSelected, quest];
    }
    helpers.setValue(newSelected);
  };

  // Toggle entire category
  const handleToggleCategory = (categoryName) => {
    const categoryQuests = displayedGroups[categoryName] || [];

    const currentSelected = field.value || [];
    const currentSelectedIds = new Set(currentSelected.map((q) => q.id));

    const allIdsInCat = categoryQuests.map((q) => q.id);
    const allSelected = allIdsInCat.every((id) => currentSelectedIds.has(id));

    let newSelected;
    if (allSelected) {
      // Deselect all visible quests in this category
      newSelected = currentSelected.filter((q) => !allIdsInCat.includes(q.id));
    } else {
      // Select all visible quests in this category (add missing ones)
      const toAdd = categoryQuests.filter((q) => !currentSelectedIds.has(q.id));
      newSelected = [...currentSelected, ...toAdd];
    }
    helpers.setValue(newSelected);
  };

  const selectedQuestIds = (field.value || []).map((q) => q.id);

  if (loading || groupedQuests === undefined) {
    return (
      <div>
        <div className="selectStudent-container">
          <div className="select-table-wrapper">
            <QuestSelectorSkeleton rows={5} />
          </div>
          <div className="select-table-wrapper">
            <QuestSelectorSkeleton rows={5} />
          </div>
        </div>
      </div>
    );
  }

  if (error || groupedQuests === null) {
    return (
      <TableError
        error={error}
        handleRefresh={getQuestByCategory}
        loading={loading}
      />
    );
  }

  return (
    <div>
      <Row className="gy-4">
        {Object.entries(displayedGroups).map(([category, quests]) => (
          <Col key={category} md={6} sm={12}>
            <SelectQuestTable
              title={category}
              quests={quests}
              selectedQuestIds={selectedQuestIds}
              onToggle={handleToggleQuest}
              onToggleAll={() => handleToggleCategory(category)}
              type="select"
            />
          </Col>
        ))}
        {Object.keys(displayedGroups).length === 0 && (
          <div className="text-center py-4 text-muted w-100">
            No quests found
          </div>
        )}
      </Row>

      {/* Validation Error */}
      {meta.touched && meta.error && (
        <div className="invalid-feedback d-block mt-2">{meta.error}</div>
      )}
    </div>
  );
};

export default QuestSelector;
